import React, { Component } from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import withStyles from '@mui/styles/withStyles';
import cx from 'classnames';
import Label from './Label';
import { onChangeWrapper } from '../../util/functions';
import { getValidationDecorations } from '../../util/validation';

/**
 * Combines the various material UI elements required to build a radio group.
 * - default theme/style
 * - wrapped with label
 * - optional wiring to provided form state
 */
class FormRadioGroup extends Component {
    render() {
        const {
            classes,
            className,
            name,
            row,
            form,
            value,
            options,
            label,
            onChange,
            onChanging,
            labelProps,
            groupProps,
            radioProps,
            labelField,
            valueField,
            disabled,
            readOnly,
            validationResult,
            ...other
        } = this.props;

        const id = `radio-${name}`;
        const { className: labelClassName, ...otherLabelProps } = labelProps;
        const { className: groupClassName, ...otherGroupProps } = groupProps;
        const currentValue = (value || (form && form.getField(name)) || '' || '') + '';

        if (readOnly) {
            const selected = options.find(v => {
                return v.value === currentValue;
            });
            return (
                <div className={classes.readOnlyBucket}>
                    <div className={cx(classes.readOnlyLabel, classes.label)}>{label}</div>
                    <div className={classes.readOnly}>{selected ? selected.label : ''}</div>
                </div>
            );
        }

        const decorations = getValidationDecorations(this.props, cx(classes.group, classes.radioContainer));

        return (
            <FormControl className={cx(className, row && classes.row)} role="group" aria-labelledby={id} {...other}>
                {label && (
                    <Label
                        className={cx(classes.label, labelClassName)}
                        id={id}
                        text={label}
                        {...otherLabelProps}
                        disabled={disabled}
                    />
                )}
                <div className={decorations.rootClass}>
                    <RadioGroup
                        className={groupClassName}
                        aria-label={label}
                        name={name}
                        value={currentValue}
                        onChange={e => onChangeWrapper(e, this)}
                        row={row}
                        {...otherGroupProps}
                    >
                        {options.map((o, i) => (
                            <FormControlLabel
                                key={i}
                                value={valueField ? o[valueField] : o.value}
                                label={labelField ? o[labelField] : o.value}
                                disabled={disabled && currentValue !== o.value}
                                classes={{ label: classes.radioLabel }}
                                control={<Radio color="primary" {...radioProps} />}
                                {...o}
                            />
                        ))}

                        {decorations.inError && (
                            <FormHelperText className={cx(classes.validationLabel, [decorations.validationLabel])}>
                                {decorations.errorMsg}
                            </FormHelperText>
                        )}
                    </RadioGroup>
                </div>
            </FormControl>
        );
    }
}

FormRadioGroup.defaultProps = {
    row: true,
    fullWidth: true,
    labelProps: {},
    groupProps: {},
    radioProps: {}
};

const styles = ({ palette, breakpoints, validationLabel }) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        [breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start'
        }
    },
    label: {
        marginRight: '1rem',
        minWidth: 90
    },
    radioLabel: {
        fontSize: '0.875rem',
        marginLeft: -6,
        marginRight: 6
    },
    radioContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        '& > div > label:last-of-type': {
            // marginRight: 0,
            '& > span:last-of-type': {
                marginRight: 0
            }
        }
    },
    readOnly: {
        whiteSpace: 'pre-wrap',
        padding: '5px 5px 5px 0',
        minHeight: 34,
        fontSize: '0.95em',
        fontStyle: 'italic'
    },
    readOnlyLabel: {
        color: '#000',
        fontWeight: '500',
        fontSize: '0.95em',
        display: 'inline-block'
    },
    validationErrorBorder: {
        margin: -1,
        marginLeft: -11,
        border: `1px solid ${palette.validation.error} !important`,
        paddingLeft: 10,
        borderRadius: 3
    },
    validationErrorLabel: {
        color: `${palette.validation.error} !important`,
        border: `1px solid ${palette.validation.error} !important`
    },
    validationSuggestedBorder: {
        margin: -1,
        marginLeft: -11,
        border: `1px solid ${palette.validation.suggested} !important`,
        paddingLeft: 10,
        borderRadius: 3
    },
    validationSuggestedLabel: {
        color: `${palette.validation.suggested} !important`,
        border: `1px solid ${palette.validation.suggested} !important`
    },
    validationOptionalBorder: {
        margin: -1,
        marginLeft: -11,
        border: `1px solid ${palette.validation.optional} !important`,
        paddingLeft: 10,
        borderRadius: 3
    },
    validationOptionalLabel: {
        color: `${palette.validation.optional} !important`,
        border: `1px solid ${palette.validation.optional} !important`
    },
    validationLabel: { ...validationLabel, marginTop: 0 }
});

export default withStyles(styles)(FormRadioGroup);
